import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  Feature,
  CreateFeatureRequest,
  UpdateFeatureRequest,
  FeatureListResponse,
  FeatureFilters,
  FeatureAnalytics,
} from '../models/feature-management.models';

// Import the config - this will be provided at the app level
export abstract class FeatureManagementConfig {
  abstract getBaseUrl(): string;
}

/**
 * Real Feature Management Service
 * This will be used in Phase 2 when the backend API is ready
 */
@Injectable({
  providedIn: 'root',
})
export class FeatureManagementService {
  constructor(
    private http: HttpClient,
    @Inject(FeatureManagementConfig) private config: FeatureManagementConfig
  ) {}

  getFeatures(filters?: FeatureFilters): Observable<FeatureListResponse> {
    const baseUrl = `${this.config.getBaseUrl()}/FeatureManagement`;
    let params = new HttpParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params = params.set(key, value.toString());
        }
      });
    }
    return this.http.get<FeatureListResponse>(baseUrl, {
      params,
    });
  }

  getFeature(id: number): Observable<Feature> {
    const baseUrl = `${this.config.getBaseUrl()}/FeatureManagement`;
    return this.http.get<Feature>(`${baseUrl}/${id}`);
  }

  createFeature(request: CreateFeatureRequest): Observable<Feature> {
    const baseUrl = `${this.config.getBaseUrl()}/FeatureManagement`;
    return this.http.post<Feature>(baseUrl, request);
  }

  updateFeature(
    id: number,
    request: UpdateFeatureRequest
  ): Observable<Feature> {
    const baseUrl = `${this.config.getBaseUrl()}/FeatureManagement`;
    return this.http.put<Feature>(`${baseUrl}/${id}`, request);
  }

  updateFeatureStatus(
    id: number,
    statusId: number
  ): Observable<Feature> {
    const baseUrl = `${this.config.getBaseUrl()}/FeatureManagement`;
    return this.http.patch<Feature>(`${baseUrl}/${id}/status`, {
      statusId
    });
  }

  addTargetingRule(id: number, rule: any): Observable<Feature> {
    const baseUrl = `${this.config.getBaseUrl()}/FeatureManagement`;
    return this.http.post<Feature>(
      `${baseUrl}/${id}/targeting-rules`,
      rule
    );
  }

  removeTargetingRule(id: number, ruleId: number): Observable<void> {
    const baseUrl = `${this.config.getBaseUrl()}/FeatureManagement`;
    return this.http.delete<void>(
      `${baseUrl}/${id}/targeting-rules/${ruleId}`
    );
  }

  // Helper methods for common status updates
  setFullAccess(id: number): Observable<Feature> {
    return this.updateFeatureStatus(id, 3); // StatusId 3 = Full Access
  }

  setDisabled(id: number): Observable<Feature> {
    return this.updateFeatureStatus(id, 1); // StatusId 1 = Disabled
  }

  setLimitedAccess(id: number): Observable<Feature> {
    return this.updateFeatureStatus(id, 2); // StatusId 2 = Limited Access
  }

  // Note: Analytics, categories, and status counts endpoints
  // are not implemented in the backend yet
  getAnalytics(featureId: string): Observable<FeatureAnalytics> {
    // TODO: Implement when backend endpoint is available
    throw new Error('Analytics endpoint not yet implemented');
  }

  getConfiguration(featureKey: string): Observable<Record<string, any>> {
    return this.http.get<Record<string, any>>(
      `${this.config.getBaseUrl()}/FeatureEvaluation/${featureKey}/configuration`
    );
  }

  getCategories(): Observable<string[]> {
    // TODO: Implement when backend endpoint is available
    throw new Error('Categories endpoint not yet implemented');
  }

  getStatusCounts(): Observable<Record<string, number>> {
    // TODO: Implement when backend endpoint is available
    throw new Error('Status counts endpoint not yet implemented');
  }
}
